import { renewalDayText, renewalReasons, renewalReasonTitles, renewalRowText } from "@teamna/shared";
import { track } from "@teamna/web-client/analytics/web.ts";
import { Button } from "../../components/index.ts";
import { firstName, householdWord } from "../families/model.ts";
import { GroupBlock, HUES, moneyKind, moneyRow, unsentMoneyRows, useNudgeSend, type Group, type HomeViewProps, type NudgeItem } from "./view.tsx";

/**
 * Renewals (Studio Home H3): where clients book for themselves, this card replaces the Running out
 * tile. It lists each client whose newest package has 2 sessions left or fewer, or ends in 10 days
 * or fewer, grouped by the reason (`renewalReason`). Each row has "Send renewal" (or "Remind to
 * pay" when money is due) and "Record payment"; "Send all" sends every row not sent yet. The words
 * of each renewal follow its reason (`nudgeMessage`).
 *
 * Pure: it draws `home.renewals` and reports the sends, so Storybook shows every state. A family
 * business has no `renewals`, and then there is no card.
 */
export function RenewalsCard(
  props: Pick<HomeViewProps, "home" | "words" | "timezone" | "businessName" | "now" | "onSend" | "onRecordPayment" | "staffPhotoOf">,
) {
  const { home, words, timezone, now } = props;
  const { busy, send } = useNudgeSend(props.onSend);
  const rows = home.renewals;
  if (!rows) return null;
  const hue = HUES.run;
  const today = home.today.date;
  const unsent = unsentMoneyRows(rows);

  function sendOne(items: NudgeItem[]) {
    if (!send) return;
    track("home_card_used", { card: "renewals", action: "send_one" });
    void send(items);
  }

  function sendAll() {
    if (!send) return;
    track("home_card_used", { card: "renewals", action: "send_all" });
    void send(unsent.map((row) => ({ participantId: row.person.participantId, kind: moneyKind(row), parent: firstName(row.person.adults[0]?.name ?? "") })));
  }

  const groups: Group[] = renewalReasons
    .map((reason) => ({
      key: reason,
      title: renewalReasonTitles[reason],
      color: reason === "low_sessions" ? "var(--color-tile-run)" : reason === "ends_soon" ? "var(--color-tile-quiet)" : "var(--color-accent)",
      rows: rows
        .filter((row) => row.reason === reason)
        .map((row) =>
          moneyRow(
            { ...row, endsText: renewalDayText(row.endsOn) },
            { words, timezone, now, businessName: props.businessName, send: send ? sendOne : null, sub: renewalRowText({ reason: row.reason, endsOn: row.endsOn, sessionsLeft: row.person.sessionsLeft }, today) },
          ),
        ),
    }))
    .filter((group) => group.rows.length > 0);

  const count = rows.length;
  return (
    <section aria-labelledby="renewals-title" className="rounded-card border border-line bg-paper px-5 py-4 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <h2 id="renewals-title" className="text-[19px]">
            {count ? `Renewals, ${count} ${householdWord(count, words)}` : "Renewals"}
          </h2>
          <p className="mt-0.5 max-w-[720px] text-[13.5px] text-ink-2">
            {count
              ? `2 sessions left or fewer, or the package ends in 10 days or fewer. A new package or a pause takes a ${words.member} off this list.`
              : `Nobody needs a renewal now. A ${words.member} comes here with 2 sessions left or fewer, or 10 days before the package ends.`}
          </p>
        </div>
        {unsent.length && send ? (
          <Button loading={busy} onClick={sendAll}>
            Send all {unsent.length} {unsent.length === 1 ? "message" : "messages"}
          </Button>
        ) : null}
      </div>
      {groups.length ? (
        <div className="mt-4 flex flex-col gap-3">
          {groups.map((group) => (
            <GroupBlock
              key={group.key}
              group={group}
              hue={hue}
              words={words}
              timezone={timezone}
              now={now}
              onRecordPayment={props.onRecordPayment}
              staffPhotoOf={props.staffPhotoOf}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
